/**
 * Editorial PDP (sections/main-product-editorial.liquid)
 * Guardrail (skills/fashion_web_design.md, Abschnitt 0): keine externen
 * API-Calls, keine App-Abhaengigkeiten, keine Beruehrung von DSers/Salestio-
 * Daten oder bestehenden Produkt-Tags.
 *
 * Funktionale Korrektheit (Varianten-Wechsel, Cart-Add) laeuft bewusst
 * NICHT hier, sondern ueber Dawns bereits verifizierte, global geladene
 * <variant-radios>/<product-form>-Web-Components (assets/global.js,
 * assets/product-form.js) - dieses Skript liefert AUSSCHLIESSLICH visuelle
 * Ergaenzungen (Galerie-Verhalten, Parallax, Ripple/Success-Feedback), ohne
 * in die Cart-/Varianten-Logik einzugreifen oder sie zu duplizieren.
 *
 * Motion-Engine (assets/motion-engine.js, echte motion.dev-Library) liefert
 * ab hier die eigentliche Spring-/Scroll-/Lens-Physik - dieses Skript nutzt
 * sie ausschliesslich ueber window.whenMotionReady()/window.MotionEngine,
 * niemals eigene Physik-Berechnung. Faellt die Engine (noch) nicht bereit
 * ist, degradiert jede Stelle unten auf reines CSS/den bisherigen
 * rAF-Scroll-Listener - keine Funktion haengt hart an der Library.
 */

/* --------------------------------------------------------------------
   MediaGallery — erfuellt exakt den Kontrakt, den Dawns VariantSelects/
   VariantRadios erwartet: eine Methode setActiveMedia(mediaId, shouldAnimate)
   auf einem Element mit id="MediaGallery-{{ section.id }}" (siehe
   assets/global.js::VariantSelects.updateMedia()). Bei einem Magazin-Grid
   (alle Bilder gleichzeitig sichtbar, kein Einzelbild-Slider) bedeutet
   "aktives Medium setzen": zur passenden Kachel scrollen + sie hervorheben,
   statt andere Bilder auszublenden - das Grid bleibt als Ganzes bestehen.
   -------------------------------------------------------------------- */
if (!customElements.get('media-gallery-editorial')) {
  customElements.define(
    'media-gallery-editorial',
    class MediaGalleryEditorial extends HTMLElement {
      connectedCallback() {
        this.tiles = Array.from(this.querySelectorAll('[data-media-id]'));
        this.tiles.forEach((tile) => tile.addEventListener('click', () => this.openLightbox(tile)));

        this.lightbox = this.querySelector('[data-lightbox]');
        if (this.lightbox) {
          this.lightbox.querySelector('[data-lightbox-close]').addEventListener('click', () => this.closeLightbox());
          this.lightbox.addEventListener('click', (event) => {
            if (event.target === this.lightbox) this.closeLightbox();
          });
          document.addEventListener('keyup', (event) => {
            if (event.code === 'Escape') this.closeLightbox();
          });
        }

        this.setupParallax();
        this.setupLens();
      }

      /**
       * Lupen-Magnifier je Kachel: das Lens-Element liegt bereits als
       * Geschwister-Node in derselben Kachel im Markup (siehe
       * sections/main-product-editorial.liquid, .editorial-gallery__lens) -
       * hier wird nur MotionEngine.lensZoom() pro Kachel/Lens-Paar verdrahtet,
       * keine eigene Zeigerlogik.
       */
      setupLens() {
        window.whenMotionReady((engine) => {
          this.tiles.forEach((tile) => {
            const lens = tile.querySelector('.editorial-gallery__lens');
            if (lens) engine.lensZoom(tile, lens, { zoom: 2.2, size: 180 });
          });
        });
      }

      setActiveMedia(mediaId, shouldAnimate) {
        const tile = this.tiles.find((el) => el.getAttribute('data-media-id') === mediaId);
        if (!tile) return;
        this.tiles.forEach((el) => el.removeAttribute('data-active'));
        tile.setAttribute('data-active', '');
        if (shouldAnimate !== false) {
          tile.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
        }
      }

      openLightbox(tile) {
        if (!this.lightbox) return;
        const img = tile.querySelector('img');
        if (!img) return;
        const lightboxImg = this.lightbox.querySelector('img');
        lightboxImg.src = img.currentSrc || img.src;
        lightboxImg.alt = img.alt;
        this.lightbox.hidden = false;
        requestAnimationFrame(() => this.lightbox.setAttribute('data-open', ''));
      }

      closeLightbox() {
        if (!this.lightbox) return;
        this.lightbox.removeAttribute('data-open');
        setTimeout(() => {
          this.lightbox.hidden = true;
        }, 300);
      }

      /**
       * Bevorzugt Motions eigene, kompositierte scroll()-Engine (echtes
       * Scroll-Linked-Animation-Feature, kein Scroll-Event-Listener noetig -
       * siehe motion-engine.js::scrollParallax()). Nur wenn die Engine object
       * keine funktionsfaehige scroll()-Implementierung mitbringt (WAAPI-
       * Fallback-Modus, siehe motion-engine.js), wird auf den bisherigen,
       * manuellen rAF-Listener zurueckgefallen - beide Pfade respektieren
       * prefers-reduced-motion (im jeweiligen Ausfuehrungspfad selbst).
       */
      setupParallax() {
        window.whenMotionReady((engine) => {
          if (engine.reducedMotion) return;
          if (engine.lib === 'motion' && typeof engine.scrollParallax === 'function') {
            this.tiles.forEach((tile) => engine.scrollParallax(tile, { distancePx: 18 }));
          } else {
            this.setupRafParallax();
          }
        });
      }

      /**
       * Leichtgewichtiger, throttled Scroll-Listener (Fallback ohne
       * Motion-Engine): verschiebt jede Kachel per transform (GPU-
       * beschleunigt) abhaengig von ihrer Position zur Bildschirmmitte -
       * reine CSS-Custom-Property-Aktualisierung, keine Layout-Neuberechnung
       * pro Frame (nur transform, keine Geometrie-Aenderung), rAF-gebunden
       * statt pro Scroll-Event.
       */
      setupRafParallax() {
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

        let ticking = false;
        const update = () => {
          const viewportCenter = window.innerHeight / 2;
          this.tiles.forEach((tile) => {
            const rect = tile.getBoundingClientRect();
            const tileCenter = rect.top + rect.height / 2;
            const distance = (tileCenter - viewportCenter) / viewportCenter;
            const offset = Math.max(Math.min(distance, 1), -1) * 18;
            tile.style.setProperty('--parallax-offset', `${offset.toFixed(2)}px`);
          });
          ticking = false;
        };

        window.addEventListener(
          'scroll',
          () => {
            if (ticking) return;
            ticking = true;
            requestAnimationFrame(update);
          },
          { passive: true }
        );
        update();
      }
    }
  );
}

/* --------------------------------------------------------------------
   Buy-Form Bugfix — haelt input[name="id"] synchron mit der aktuell
   gewaehlten Variante (Wert + disabled-Status).

   Hintergrund (siehe DEPLOY_NOTES.md fuer die volle Herleitung): diese
   Section erwartet laut Kommentar oben, dass assets/product-form.js
   geladen ist - ist es auf der Live-Seite aktuell aber NICHT (kein
   Script-Tag). Dawns VariantSelects (assets/global.js) aktualisiert bei
   einem Variantenwechsel per updateVariantInput() zwar den WERT von
   input[name="id"], fasst aber nie dessen disabled-Attribut an - das ist
   normalerweise Aufgabe von product-form.js's eigenem <product-form>-
   Element. Die Section rendert input[name="id"] serverseitig disabled;
   ohne product-form.js bleibt es das dauerhaft. Ein disabled-Formularfeld
   wird vom Browser nie mitgesendet, was den beobachteten Live-Bug erklaert:
   POST /cart/add schlaegt mit HTTP 400 ("Required parameter missing:
   items") fehl, weil `id` im Payload komplett fehlt.

   Diese Funktion ersetzt product-form.js NICHT (kein eigener Cart-Add-
   Request, keine eigene Fehlerbehandlung - das bleibt bewusst Dawns
   Aufgabe, siehe Guardrail oben) und rueckt nur exakt das eine fehlende
   Stueck Logik nach: input[name="id"] anhand der aktuell gewaehlten
   Optionen synchron halten, sowohl beim initialen Laden (Dawns
   VariantSelects ruft onVariantChange() nie automatisch fuer den
   Server-gerenderten Ausgangszustand auf) als auch bei jedem weiteren
   Variantenwechsel. Bleibt ein Kombination sold-out (available:false),
   bleibt/wird das Feld bewusst weiterhin disabled - kein Freischalten
   von nicht kaufbaren Varianten.
   -------------------------------------------------------------------- */
document.querySelectorAll('variant-radios').forEach((variantRadios) => {
  const form = variantRadios.closest('form');
  const dataScript = variantRadios.querySelector('script[type="application/json"]');
  if (!form || !dataScript) return;

  const idInput = form.querySelector('input[name="id"]');
  if (!idInput) return;

  let variants;
  try {
    variants = JSON.parse(dataScript.textContent);
  } catch (error) {
    console.error('[editorial-pdp] Varianten-JSON konnte nicht gelesen werden', error);
    return;
  }

  const syncIdInput = () => {
    const selectedOptions = Array.from(variantRadios.querySelectorAll('fieldset')).map((fieldset) => {
      const checked = fieldset.querySelector('input[type="radio"]:checked');
      return checked ? checked.value : null;
    });
    if (selectedOptions.some((value) => value === null)) return;

    const matched = variants.find((variant) =>
      variant.options.every((option, index) => option === selectedOptions[index])
    );
    if (!matched) return;

    idInput.value = matched.id;
    idInput.disabled = !matched.available;
  };

  syncIdInput();
  variantRadios.addEventListener('change', syncIdInput);
});

/* --------------------------------------------------------------------
   Editorial CTA — Ripple-on-Click + Success-/Error-aware Feedback +
   AJAX-Submit-Bugfix.

   Ripple/Observer-Teil unveraendert: reagiert NUR auf die .loading-Klasse,
   die eigentlich <product-form> (assets/product-form.js) selbst setzen
   wuerde. Da dieses Skript auf der Live-Section nicht eingebunden ist
   (siehe DEPLOY_NOTES.md), setzt/entfernt bislang NICHTS die .loading-
   Klasse - der Observer wartete auf ein Ereignis, das nie eintrat, und
   das Formular submittete stattdessen klassisch (volle Seitennavigation
   zu /cart/add statt eines AJAX-Requests, der die Seite nicht verlaesst).

   Der neue submit-Handler unten schliesst genau diese Luecke, im selben
   Stil wie das bereits korrekte assets/style-advisor.js::addAllToCart()
   (fetch + JSON-Body + Cart-Drawer-Sections-Refresh) - kein neu erfundenes
   Muster, nur dieselbe, bereits im Theme vorhandene Vorgehensweise auf den
   Haupt-Kaufbutton uebertragen. Dupliziert weiterhin keine Varianten-Logik
   (input[name="id"] kommt vom Buy-Form-Bugfix oben bzw. von Dawns
   VariantSelects) und ruft keine externen APIs auf.
   -------------------------------------------------------------------- */
document.querySelectorAll('.editorial-cta').forEach((button) => {
  button.addEventListener('click', (event) => {
    const rect = button.getBoundingClientRect();
    const ripple = document.createElement('span');
    ripple.className = 'editorial-cta__ripple';
    ripple.style.left = `${event.clientX - rect.left}px`;
    ripple.style.top = `${event.clientY - rect.top}px`;
    const layer = button.querySelector('[data-ripple-layer]') || button;
    layer.appendChild(ripple);
    ripple.addEventListener('animationend', () => ripple.remove());
  });

  let wasLoading = false;
  const observer = new MutationObserver(() => {
    const isLoading = button.classList.contains('loading');
    if (isLoading && !wasLoading) {
      button.classList.add('is-submitting');
    } else if (!isLoading && wasLoading) {
      button.classList.remove('is-submitting');
      const succeeded = button.getAttribute('aria-disabled') !== 'true';
      if (succeeded) {
        button.classList.add('is-success');
        window.whenMotionReady((engine) => engine.drawCheckmark(button.querySelector('.editorial-cta__checkmark path')));
        setTimeout(() => button.classList.remove('is-success'), 1600);
      }
    }
    wasLoading = isLoading;
  });
  observer.observe(button, { attributes: true, attributeFilter: ['class'] });

  window.whenMotionReady((engine) => {
    engine.magneticGlow(button);
    engine.pressFeedback(button);
  });

  const form = button.closest('form');
  if (!form || form.dataset.editorialAjaxBound) return;
  form.dataset.editorialAjaxBound = 'true';

  form.addEventListener('submit', (event) => {
    event.preventDefault();
    const errorWrapper = form.querySelector('.product-form__error-message-wrapper');
    const errorMessage = form.querySelector('.product-form__error-message');

    button.classList.add('loading');
    button.removeAttribute('aria-disabled');
    if (errorWrapper) errorWrapper.hidden = true;

    const formData = new FormData(form);

    // Welches Cart-UI-Element tatsaechlich geladen ist (cart-drawer ODER
    // cart-notification, siehe DEPLOY_NOTES.md), wird VOR dem Request
    // ermittelt, weil jedes seine eigenen, unterschiedlichen Section-IDs
    // ueber getSectionsToRender() erwartet (cart-drawer: "cart-drawer" +
    // "cart-icon-bubble"; cart-notification: "cart-notification-product" +
    // "cart-notification-button" + "cart-icon-bubble"). Fix fuer den
    // beobachteten Bug: das hartkodierte sections-Array war 1:1 von
    // assets/style-advisor.js::addAllToCart() uebernommen, das cart-drawer
    // annimmt - auf dieser Live-Seite ist aber cart-notification geladen,
    // daher fehlte "cart-notification-product" in der Response und
    // CartNotification.getSectionInnerHTML() griff auf ein nicht
    // existierendes Element zu (TypeError: ... reading 'innerHTML').
    const cartUi = document.querySelector('cart-drawer, cart-notification');
    const sections =
      cartUi && typeof cartUi.getSectionsToRender === 'function'
        ? [...new Set(cartUi.getSectionsToRender().map((section) => section.id))]
        : ['cart-icon-bubble'];

    // Einzelnes Item -> Legacy-Single-Item-Request-Format ({id, quantity}),
    // NICHT das items-Array-Format ({items:[...]})  das assets/style-advisor.js
    // fuer sein Mehrfach-Add nutzt. Grund (2. gefundener Bug, siehe
    // DEPLOY_NOTES.md): /cart/add.js antwortet auf das items-Array-Format mit
    // {items:[...], sections:{...}} (kein Top-Level "key"/"id"), auf das
    // Single-Item-Format aber mit dem Line-Item-Objekt direkt ({key, id, ...,
    // sections:{...}}) - genau die Form, die sowohl CartNotification.
    // renderContents() (liest parsedState.key) als auch CartDrawer.
    // renderContents() (liest parsedState.id) erwarten. Mit dem Array-Format
    // blieb parsedState.key/id "undefined", wodurch CartNotification den
    // Produkt-Section-Selector "[id=cart-notification-product-undefined]"
    // baute, der nie traf -> TypeError beim Zugriff auf .innerHTML von null.
    fetch(window.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        id: formData.get('id'),
        quantity: Number(formData.get('quantity')) || 1,
        sections,
        sections_url: window.location.pathname,
      }),
    })
      .then((response) => response.json().then((data) => ({ ok: response.ok, data })))
      .then(({ ok, data }) => {
        if (!ok) throw new Error(data.description || data.message || 'Cart-Add fehlgeschlagen');
        if (cartUi && typeof cartUi.renderContents === 'function') {
          cartUi.renderContents(data);
        }
        button.classList.remove('loading');
      })
      .catch((error) => {
        console.error('[editorial-pdp]', error);
        button.classList.remove('loading');
        button.setAttribute('aria-disabled', 'true');
        if (errorWrapper && errorMessage) {
          errorMessage.textContent = error.message;
          errorWrapper.hidden = false;
        }
      });
  });
});

/* --------------------------------------------------------------------
   Varianten-Pills — Glow-Tracking + Spring-Press, rein visuell, greift
   nicht in <variant-radios> (assets/global.js) ein.
   -------------------------------------------------------------------- */
window.whenMotionReady((engine) => {
  document.querySelectorAll('.editorial-variants__pill').forEach((pill) => {
    engine.magneticGlow(pill);
    engine.pressFeedback(pill);
  });
});
